import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { BButton, BEmpty, BInput, BModal } from "components/base";
import { observer } from "mobx-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { chatStore } from "store/chatStore";
import SearchChatResultItem from "./SearchChatResultItem";
import "./SearchChatModal.scss";

export type SearchChatModalProps = {
  open: boolean;
  onClose: () => void;
};

const SearchChatModal = observer(function SearchChatModal({
  open,
  onClose,
}: SearchChatModalProps) {
  const [query, setQuery] = useState("");
  const { conversations, selectedConversationId, selectedProject } = chatStore;

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const title = conversation.name || `Chat #${conversation.id}`;
      if (!q) {
        return true;
      }
      return title.toLowerCase().includes(q);
    });
  }, [conversations, query]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      void chatStore.selectConversation(Number(id));
      onClose();
    },
    [onClose],
  );

  const projectSubtitle = selectedProject?.name;
  const sectionTitle = query.trim() ? "Lịch sử chat" : "Lịch sử chat";

  return (
    <BModal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      closable={false}
      destroyOnClose
      width={540}
      className="search-chat-modal"
    >
      <div className="search-chat-modal__content">
        <div className="search-chat-modal__toolbar">
          <BInput
            className="search-chat-modal__input"
            value={query}
            onChange={handleQueryChange}
            placeholder="Search..."
            prefix={<SearchOutlined className="search-chat-modal__input-icon" />}
            allowClear
            autoFocus
            aria-label="Search chats"
          />
          <BButton
            type="text"
            className="search-chat-modal__close"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label="Close"
            title="Close"
          />
        </div>

        <div className="search-chat-modal__body">
          <h3 className="search-chat-modal__section-title">{sectionTitle}</h3>

          {results.length === 0 ? (
            <div className="search-chat-modal__empty">
              <BEmpty
                description={
                  query.trim()
                    ? "Không tìm thấy kết quả phù hợp"
                    : "Chưa có lịch sử chat nào"
                }
              />
            </div>
          ) : (
            <ul className="search-chat-modal__list">
              {results.map((conversation) => {
                const title = conversation.name || `Chat #${conversation.id}`;
                return (
                  <li key={conversation.id} className="search-chat-modal__row">
                    <SearchChatResultItem
                      id={String(conversation.id)}
                      title={title}
                      subtitle={conversation.project?.name ?? projectSubtitle}
                      active={selectedConversationId === conversation.id}
                      onSelect={handleSelect}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </BModal>
  );
});

export default SearchChatModal;
